import { HTMLAttributes } from 'react'

import Container from '@/components/base/Container'
import Grid from '@/components/base/Grid'
import Image from '@/components/base/Image'
import MediaParallax from '@/components/base/MediaParallax'
import Motion from '@/components/base/Motion'
import Typography from '@/components/base/Typography'

import { tripleSection } from './animations'
import { cn } from '@/lib/utils'
import { WPImage, WPPageData } from '@/types'

export type TripleSectionProps = HTMLAttributes<HTMLElement> &
  Partial<{
    theme: WPPageData['acf']['theme']
    first_column: {
      image: WPImage
      title: string
    }
    middle_column: {
      title: string
      text_1: string
      text_2: string
    }
    third_column: {
      image: WPImage
      text: string
    }
  }>

const TripleSection = ({
  first_column,
  middle_column,
  third_column,
  theme,
  ...props
}: TripleSectionProps) => {
  return (
    <Motion animation={tripleSection}>
      {(motionRef) => (
        <section
          ref={motionRef}
          {...props}
          className={cn([
            {
              'bg-background text-foreground': theme === 'light',
              'bg-black text-white': theme === 'dark'
            }
          ])}
        >
          <Container>
            <Grid className="gap-y-[5.5rem]">
              {first_column ? (
                <div
                  className={cn([
                    'col-span-4 flex flex-col gap-[1.6rem]',
                    'md:col-span-4 md:gap-[3.2rem]'
                  ])}
                >
                  {first_column.image?.url ? (
                    <MediaParallax
                      className={cn([
                        'aspect-[453/540]'
                        // 'after:absolute after:left-0 after:top-0 after:z-20 after:h-full after:w-full after:bg-[radial-gradient(50%_50%_at_50%_50%,rgba(0,0,0,0)_0%,rgba(0,0,0,0.3)_100%)]'
                      ])}
                    >
                      {(ref: React.RefObject<HTMLElement>) => (
                        <Image
                          ref={ref as React.RefObject<HTMLImageElement>}
                          source={first_column.image}
                          fit="contain"
                          data-motion-image
                        />
                      )}
                    </MediaParallax>
                  ) : null}
                </div>
              ) : null}
              {middle_column ? (
                <div
                  className={cn([
                    'col-span-4 flex flex-col gap-[3rem]',
                    'md:col-span-3 md:col-start-6 md:gap-[6rem]'
                  ])}
                >
                  {middle_column.title ? (
                    <Typography
                      variant="h1"
                      component="h2"
                      className="uppercase text-[#591717]"
                      data-motion-title
                    >
                      {middle_column.title}
                    </Typography>
                  ) : null}
                  {middle_column.text_1 ? (
                    <Typography variant="body" data-motion-paragraph>
                      {middle_column.text_1}
                    </Typography>
                  ) : null}
                </div>
              ) : null}
              {third_column ? (
                <div
                  className={cn([
                    'col-span-4 flex flex-col gap-[3rem]',
                    'md:col-span-3 md:col-start-10 md:gap-[6rem]'
                  ])}
                >
                  {third_column.image?.url ? (
                    <MediaParallax
                      className={cn([
                        'aspect-square'
                        // 'after:absolute after:left-0 after:top-0 after:z-20 after:h-full after:w-full after:bg-[radial-gradient(50%_50%_at_50%_50%,rgba(0,0,0,0)_0%,rgba(0,0,0,0.3)_100%)]'
                      ])}
                    >
                      {(ref: React.RefObject<HTMLElement>) => (
                        <Image
                          ref={ref as React.RefObject<HTMLImageElement>}
                          source={third_column.image}
                          fit="contain"
                          data-motion-secondary-image
                        />
                      )}
                    </MediaParallax>
                  ) : null}
                </div>
              ) : null}
              {first_column?.title ? (
                <div
                  className={cn([
                    'col-span-4 flex flex-col gap-[1.6rem]',
                    'md:col-span-4 md:col-start-1 md:gap-[3.2rem]'
                  ])}
                >
                  <div className="flex">
                    <Typography
                      variant="big"
                      className="-mt-[4rem] uppercase text-[#591717]"
                      data-motion-big-title
                    >
                      {first_column.title}
                    </Typography>
                  </div>
                </div>
              ) : null}
              {middle_column?.text_2 ? (
                <div
                  className={cn([
                    'col-span-4 flex flex-col gap-[3rem]',
                    'md:col-span-3 md:col-start-6 md:gap-[6rem]'
                  ])}
                >
                  {middle_column.text_2 ? (
                    <Typography
                      variant="h2"
                      component="h3"
                      className="uppercase text-[#591717]"
                      data-motion-subtitle
                    >
                      {middle_column.text_2}
                    </Typography>
                  ) : null}
                </div>
              ) : null}
              {third_column?.text ? (
                <div
                  className={cn([
                    'col-span-4 flex flex-col gap-[3rem]',
                    'md:col-span-3 md:col-start-10 md:gap-[6rem]'
                  ])}
                >
                  <Typography variant="body" data-motion-paragraph>
                    {third_column.text}
                  </Typography>
                </div>
              ) : null}
            </Grid>
          </Container>
        </section>
      )}
    </Motion>
  )
}

export default TripleSection
